import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserTie, FaUser, FaCheckCircle, FaSearch, FaHandshake, FaMoneyBillWave } from 'react-icons/fa';
import PageTransition from '../components/ui/PageTransition';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';

const steps = {
    worker: [
        {
            title: "Create Profile",
            description: "Sign up and build your professional profile. List your skills, experience, and certifications.",
            icon: <FaUserTie className="text-4xl text-brand-primary" />
        },
        {
            title: "Get Verified",
            description: "Our admin team will review your profile. Once verified, you'll gain the 'Verified' badge.",
            icon: <FaCheckCircle className="text-4xl text-blue-500" />
        },
        {
            title: "Find Jobs",
            description: "Browse available jobs or wait for direct offers from buyers.",
            icon: <FaSearch className="text-4xl text-amber-500" />
        },
        {
            title: "Get Hired & Paid",
            description: "Accept offers, complete the work, and get paid securely through the platform.",
            icon: <FaMoneyBillWave className="text-4xl text-green-600" />
        }
    ],
    buyer: [
        {
            title: "Sign Up",
            description: "Create a buyer account to start looking for skilled agricultural workers.",
            icon: <FaUser className="text-4xl text-brand-primary" />
        },
        {
            title: "Post a Job",
            description: "Describe your needs, set a budget, and post a job for bidding or direct hire.",
            icon: <FaSearch className="text-4xl text-blue-500" />
        },
        {
            title: "Hire Workers",
            description: "Review bids or profiles, interview candidates, and hire the best fit.",
            icon: <FaHandshake className="text-4xl text-amber-500" />
        },
        {
            title: "Manage & Pay",
            description: "Track progress and release payment upon successful completion.",
            icon: <FaMoneyBillWave className="text-4xl text-green-600" />
        }
    ]
};

const GuidePage = () => {
    const [activeTab, setActiveTab] = useState('worker');

    return (
        <PageTransition>
            <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-extrabold text-brand-dark sm:text-5xl sm:tracking-tight lg:text-6xl">
                            How It Works
                        </h1>
                        <p className="mt-5 max-w-xl mx-auto text-xl text-slate-500">
                            Your guide to succeeding on Krishok Lagbe.
                        </p>
                    </div>

                    <div className="flex justify-center mb-12">
                        <div className="bg-white p-1 rounded-xl shadow-md inline-flex">
                            <button
                                onClick={() => setActiveTab('worker')}
                                className={`px-8 py-3 rounded-lg text-lg font-medium transition-all ${activeTab === 'worker'
                                        ? 'bg-brand-primary text-white shadow-lg'
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                I am a Worker
                            </button>
                            <button
                                onClick={() => setActiveTab('buyer')}
                                className={`px-8 py-3 rounded-lg text-lg font-medium transition-all ${activeTab === 'buyer'
                                        ? 'bg-brand-secondary text-white shadow-lg'
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                I am a Buyer
                            </button>
                        </div>
                    </div>

                    <div className="relative">
                        <AnimatePresence mode='wait'>
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="grid gap-8 md:grid-cols-2 lg:grid-cols-4"
                            >
                                {steps[activeTab].map((step, index) => (
                                    <Card key={index} className="relative overflow-hidden group hover:border-brand-primary/30 transition-colors">
                                        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-brand-light rounded-full opacity-50 blur-xl group-hover:scale-150 transition-transform duration-500"></div>

                                        <div className="relative z-10 flex flex-col items-center text-center h-full">
                                            <div className="mb-6 p-4 bg-slate-50 rounded-full shadow-inner">
                                                {step.icon}
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                                            <p className="text-slate-600 leading-relaxed">
                                                {step.description}
                                            </p>

                                            {/* Step Number */}
                                            <div className="mt-auto pt-6">
                                                <span className="text-6xl font-extrabold text-slate-100 absolute bottom-2 right-4 -z-0">
                                                    {index + 1}
                                                </span>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="mt-20 text-center">
                        <h2 className="text-3xl font-bold text-brand-dark mb-6">Ready to get started?</h2>
                        <div className="flex justify-center gap-4">
                            <Button variant="primary" onClick={() => window.location.href = '/auth/register'}>
                                Create Account
                            </Button>
                            <Button variant="outline" onClick={() => window.location.href = '/jobs'}>
                                Browse Jobs
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </PageTransition>
    );
};

export default GuidePage;
